<template>
  <Teleport to="body">
    <div
      v-if="isContainerShown"
      class="tw-fixed tw-inset-0 tw-z-[1000] tw-grid tw-h-screen tw-w-screen tw-place-items-center"
      @mousedown="handleOutsideClick"
      :id="backdropId"
      ref="modalRef"
    >
      <transition name="fade">
        <div
          v-if="isBackdropShown"
          class="tw-absolute tw-z-[1001] tw-h-screen tw-w-full tw-bg-black/75"
          :class="{ 'tw-backdrop-blur-sm': isBackgroundBlurred }"
        />
      </transition>
      <transition
        enter-active-class="tw-transition tw-ease-out"
        enter-from-class="tw-scale-95 tw-opacity-0 tw--translate-y-4"
        enter-to-class="tw-scale-100 tw-opacity-100 tw--translate-y-0"
        leave-active-class="tw-ease-out"
        leave-from-class="tw-scale-100 tw-opacity-100 tw--translate-y-0"
        leave-to-class="tw-scale-95 tw-opacity-0 tw--translate-y-4"
      >
        <div
          v-if="isModalShown"
          :class="
            twMerge(
              'tw-z-[1002] tw-grid tw-h-full tw-w-screen tw-place-items-center tw-overflow-y-auto tw-px-4 tw-pb-32',
              modalClass,
            )
          "
        >
          <div
            role="dialog"
            aria-modal="true"
            :id="id"
            :class="
              twMerge(
                'tw-mx-auto tw-w-full tw-rounded-xl tw-bg-white',
                widthClass,
              )
            "
          >
            <FocusLoop :is-active="isFocusLoopActive" :auto-focus="autoFocus">
              <slot />
            </FocusLoop>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script setup>
import { getUniqueId } from '@/shared/utils/uniqueId.js'
import FocusLoop from './FocusLoop.vue'
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useModalsStore } from '@/shared/stores/modals.js'
import { twMerge } from 'tailwind-merge'

const emit = defineEmits(['show', 'hide'])

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  widthClass: {
    type: String,
    default: '',
  },
  modalClass: {
    type: String,
    default: 'tw-pt-4',
  },
  clickOutsideToClose: {
    type: Boolean,
    default: true,
  },
  isBackgroundBlurred: {
    type: Boolean,
    default: false,
  },
  autoFocus: {
    type: Boolean,
    default: true,
  },
})

const { activeModals, closeModal } = useModalsStore()

const id = `growthmentor-modal-${getUniqueId()}`
const backdropId = `growthmentor-modal-backdrop-${getUniqueId()}`
const isVisible = ref(false)
const isContainerShown = ref(false)
const isBackdropShown = ref(false)
const isModalShown = ref(false)
const isFocusLoopActive = ref(false)
const modalRef = ref(null)

watch(activeModals, (newActiveModals) => {
  isVisible.value = newActiveModals.includes(props.name)
})

watch(isVisible, (newValue) => {
  if (newValue) {
    lockBody()
    showModal()
    emit('show')
  } else {
    unlockBody()
    hideModal()
    emit('hide')
  }
})

const lockBody = () => {
  const scrollBarWidth =
    window.innerWidth - document.documentElement.clientWidth
  document.body.style.paddingRight = `${scrollBarWidth}px`
  document.body.style.overflow = 'hidden'
}

const unlockBody = () => {
  document.body.style.paddingRight = '0px'
  document.body.style.overflow = 'auto'
}

const animateModal = () => {
  document
    .getElementById(id)
    .animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.02)' },
        { transform: 'scale(1)' },
      ],
      {
        duration: 200,
      },
    )
}

const handleOutsideClick = (event) => {
  if (!isVisible.value) return

  // Multiselect/select specific (these elements disappear when clicking on input, so it's being detected as a click outside)
  const selectInputClasses = [
    'multiselect__placeholder',
    'multiselect__single',
    'multiselect__tag-icon',
  ]
  const isElementFromSelectInput = selectInputClasses.some((selectInputClass) =>
    event.target.classList.contains(selectInputClass),
  )

  const isClickedElementInsideModal = document
    .getElementById(id)
    .contains(event.target)

  if (isClickedElementInsideModal || isElementFromSelectInput) return

  handleCloseModal()
}

const handleClickEscapeKey = (event) => {
  if (!isVisible.value || event.keyCode !== 27) return

  handleCloseModal()
}

const handleCloseModal = () => {
  if (!props.clickOutsideToClose) {
    animateModal()
    return
  }

  closeModal(props.name)
}

const showModal = () => {
  isContainerShown.value = true

  nextTick(() => {
    isBackdropShown.value = true
    isModalShown.value = true
    isFocusLoopActive.value = true

    document.addEventListener('keyup', handleClickEscapeKey)
  })
}

const hideModal = () => {
  isFocusLoopActive.value = false

  nextTick(() => {
    isModalShown.value = false
    isBackdropShown.value = false
    isContainerShown.value = false

    document.removeEventListener('keyup', handleClickEscapeKey)
  })
}

// Make sure that the modal does not stay in the DOM after the component is destroyed
onBeforeUnmount(() => {
  unlockBody()

  document.removeEventListener('keyup', handleClickEscapeKey)

  const modalElement = document.getElementById(id)
  if (modalElement) modalElement.remove()

  const modalBackdropElement = document.getElementById(backdropId)
  if (modalBackdropElement) modalBackdropElement.remove()
})
</script>
